import {
    NotificationsMenu as NotificationsMenuBase,
    type NotificationsMenuInvite,
    showInviteAcceptedToast,
    showInviteDeclinedToast,
    toaster,
} from '@ionq/legacy-ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useRevalidator } from 'react-router';
import { latchSessionRevalidation } from '../lib/sessionRevalidation';
import { queryClient, sessionUserQueryOptions, trpc } from '../lib/trpc';
import { useSession } from './SessionProvider';

// Data/toast wiring for the shared notifications bell (legacy-ui renders the
// popover; this adapter owns the query, mutations and switch navigation) —
// mirrored by the enterprise app's sibling adapter.

export function NotificationsMenu() {
    const revalidator = useRevalidator();
    const { organizations, switchOrganization } = useSession();
    // The toast action goes through state, not a direct call: the click can
    // land before the revalidated session list has committed, and
    // switchOrganization silently no-ops on an org missing from its
    // snapshot. The effect performs the switch on the first render where
    // the target org is actually in the list.
    const [switchTargetOrgId, setSwitchTargetOrgId] = useState<string | null>(
        null,
    );
    useEffect(() => {
        if (!switchTargetOrgId) return;
        if (!organizations.some((org) => org.id === switchTargetOrgId)) return;
        setSwitchTargetOrgId(null);
        switchOrganization(switchTargetOrgId);
    }, [switchTargetOrgId, organizations, switchOrganization]);

    // throwOnError: false — the client default would escalate a failed
    // list fetch to the route errorElement; the popover renders its own
    // error panel instead.
    const invites = useQuery(
        trpc.invites.list.queryOptions(undefined, { throwOnError: false }),
    );
    const accept = useMutation(trpc.invites.accept.mutationOptions());
    const decline = useMutation(trpc.invites.decline.mutationOptions());

    // Busy spans the WHOLE flow, not just the mutation: the acted-on row
    // stays rendered until the awaited list refetch reconciles it, and a
    // re-enabled button in that window would fire a second request whose
    // 404/409 toast contradicts the success that already happened.
    const [acting, setActing] = useState(false);

    // Awaited: the bell observes this query, so resolution means the acted-on
    // row is gone before the buttons re-enable.
    const refreshInvites = () =>
        queryClient.invalidateQueries({
            queryKey: trpc.invites.list.queryKey(),
        });

    const onAccept = async (invite: NotificationsMenuInvite) => {
        setActing(true);
        try {
            try {
                await accept.mutateAsync({ inviteId: invite.id });
            } catch (error) {
                // Upstream 404/409 messages are user-readable and specific.
                toaster.error({
                    title:
                        error instanceof Error
                            ? error.message
                            : 'Could not accept the invite. Please try again.',
                });
                await refreshInvites();
                return;
            }
            // Refresh the switcher BEFORE the toast: the session list is a
            // root loader snapshot pinned at staleTime Infinity, and the
            // toast's switch action needs the new org in it — invalidate,
            // latch (parity with switchOrganization's own flow), then await
            // the revalidation that re-runs sessionLoader against the stale
            // entry.
            await queryClient.invalidateQueries({
                queryKey: sessionUserQueryOptions().queryKey,
            });
            latchSessionRevalidation();
            await revalidator.revalidate();
            await refreshInvites();
            showInviteAcceptedToast({
                organizationName: invite.organizationName,
                onGoToOrg: () => setSwitchTargetOrgId(invite.organizationId),
            });
        } finally {
            setActing(false);
        }
    };

    const onDecline = async (invite: NotificationsMenuInvite) => {
        setActing(true);
        try {
            try {
                await decline.mutateAsync({ inviteId: invite.id });
            } catch (error) {
                toaster.error({
                    title:
                        error instanceof Error
                            ? error.message
                            : 'Could not decline the invite. Please try again.',
                });
                await refreshInvites();
                return;
            }
            await refreshInvites();
            showInviteDeclinedToast();
        } finally {
            setActing(false);
        }
    };

    return (
        <NotificationsMenuBase
            invites={invites.data}
            error={invites.isError}
            busy={acting}
            onAccept={(invite) => void onAccept(invite)}
            onDecline={(invite) => void onDecline(invite)}
        />
    );
}
